import AuthService from "../services/auth.service";
import {
  buildRuntimeAuthUser,
  clearLegacyAuthStorage,
  persistSafeAuthUser,
  readSafeStoredUser,
} from "@/utils/authStorage";
import {
  DARK_THEME,
  getPreferredTheme,
  LIGHT_THEME,
  setPreferredTheme,
} from "@/theme/applyTheme";
import { isUnauthorizedError } from "@/utils/authFlow";

const user = readSafeStoredUser();
const mode = getPreferredTheme() === DARK_THEME;
const initialState = user
  ? { status: { loggedIn: true, dark: mode }, user }
  : { status: { loggedIn: false, dark: mode }, user: null };

export function createInitialAuthState() {
  const storedUser = readSafeStoredUser();
  const darkMode = getPreferredTheme() === DARK_THEME;
  return storedUser
    ? { status: { loggedIn: true, dark: darkMode }, user: storedUser }
    : { status: { loggedIn: false, dark: darkMode }, user: null };
}

export const auth = {
  namespaced: true,
  state: initialState,
  getters: {
    selectedClientName(state) {
      const user = state.user;
      if (!user || !Array.isArray(user.clients)) return null;
      const client = user.clients.find(
        (c) => String(c.id) === String(user.selectedClient)
      );
      return client ? client.name : null;
    },
  },
  actions: {
    login({ commit }, user) {
      return AuthService.login(user)
        .then((user) => {
          if (!user.permissions.includes("claims-process")) {
            AuthService.logout();
            commit("logout");
            window.location.assign(process.env.VUE_APP_CUSTOMER_PORTAL_BASE_URL);
          }
          commit("loginSuccess", user);
          return Promise.resolve(user);
        })
        .catch((error) => {
          commit("loginFailure");
          return Promise.reject(error);
        });
    },
    checkToken({ commit }) {
      return AuthService.checkToken()
        .then((user) => {
          commit("loginSuccess", user);
          return Promise.resolve(user);
        })
        .catch((error) => {
          if (isUnauthorizedError(error)) {
            commit("loginFailure");
          }

          return Promise.reject(error);
        });
    },
    logout({ commit }) {
      return AuthService.logout().then(() => {
        commit("logout");
      });
    },
    selectClient({ commit }, client) {
      commit("selectClient", client);
    },
    changeMode({ commit }, mode) {
      commit("changeMode", mode);
    },
  },
  mutations: {
    loginSuccess(state, user) {
      const safeUser = buildRuntimeAuthUser(user);
      if (
        safeUser &&
        [
          "a.gore@varstechnology.co.uk",
          "j.higgins@varstechnology.co.uk",
        ].includes(safeUser.email)
      ) {
        window.location.assign(process.env.VUE_APP_ADMIN_PORTAL_BASE_URL);
      }
      state.status.loggedIn = true;
      state.user = safeUser;
      persistSafeAuthUser(safeUser);
    },
    loginFailure(state) {
      state.status.loggedIn = false;
      state.user = null;
    },
    logout(state) {
      state.status.loggedIn = false;
      state.user = null;
      clearLegacyAuthStorage();
      window.location.href = process.env.VUE_APP_GATEWAY_PORTAL_BASE_URL;
      return;
    },
    selectClient(state, client) {
      state.user = buildRuntimeAuthUser(state.user);
      state.user.selectedClient = client.id;
      persistSafeAuthUser(state.user);
    },
    changeMode(state, mode) {
      const appliedTheme = setPreferredTheme(mode ? DARK_THEME : LIGHT_THEME);

      state.status.dark = appliedTheme === DARK_THEME;
    },
  },
};
